/*
 * Copyright (c) 2025 - 2026. Bluemate B.V.
 */
import './style/infoBanner.scss';

import classNames from 'classnames';

import React, {PropsWithChildren, useContext, useMemo, useState, MouseEventHandler, ReactNode} from 'react';

import {useTranslation} from 'react-i18next';

import {BluemateControlColor} from '../../util/types';

import {bannerSessionKey, getFromSession, saveToSession} from '../../util/storageUtil';

import {createSnoozeFor, SnoozeConfig} from '../../util/snoozeUtil';

import {AppContext} from './appContext';

import {AwesomeIcon, Link} from '@dvrd/dvr-controls';

import InfoIcon from '../icon/infoIcon';

import WarnIcon from '../icon/warnIcon';

import ErrorIcon from '../icon/errorIcon';

import SuccessIcon from '../icon/successIcon';

import {BluemateButton} from '../controls';

type BannerVariant = 'info' | 'success' | 'warning' | 'critical';

interface Props {
    featureKey?: string;
    className?: string;
    closeText?: string;
    variant?: BannerVariant;
    title?: string;
    actionLabel?: string;
    onAction?: MouseEventHandler;
    /** When true, the action button shows an inline spinner and is disabled
     *  (prevents a second tap while the action is in flight). */
    actionLoading?: boolean;
    actionRoute?: string;
    icon?: ReactNode;
    persistDismiss?: boolean;
    /** Dismissal lasts for the current login session only (sessionStorage,
     *  cleared by every flow that establishes a new login), so the banner shows
     *  again on every new login. Requires `featureKey`; wins over
     *  `persistDismiss`. */
    sessionDismiss?: boolean;
    /** Dismissal snoozes the banner for an escalating cooldown (localStorage,
     *  see snoozeUtil), after which it returns. Wins over `sessionDismiss` and
     *  `persistDismiss`. */
    snooze?: SnoozeConfig;
    onClose?: MouseEventHandler;
    primaryBannerButton?: boolean;
    showForNewUsersOnly?: boolean;
}

export default function InfoBanner(props: PropsWithChildren<Props>) {
    const {
        featureKey,
        children,
        className,
        variant = 'info',
        title,
        actionLabel,
        onAction,
        actionLoading = false,
        icon,
        persistDismiss = true,
        sessionDismiss = false,
        snooze,
        onClose,
        primaryBannerButton = false,
        showForNewUsersOnly = false
    } = props;
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const flexKey = useMemo(() => (featureKey ? `feature_intro_${featureKey}_closed` : null), [featureKey]);
    const sessionKey = useMemo(() => (featureKey ? bannerSessionKey(featureKey) : null), [featureKey]);
    const [isClosed, setIsClosed] = useState(() => {
        if (snooze) return createSnoozeFor(snooze).isSnoozed();
        if (sessionDismiss && sessionKey) return getFromSession(sessionKey) === '1';
        return persistDismiss && flexKey && user ? user.getFlex(flexKey) === true : false;
    });

    const buttonProps = useMemo((): {secondary: boolean; color: BluemateControlColor} => {
        const secondary = !primaryBannerButton;

        switch (variant) {
            case 'success':
                return {secondary, color: 'green'};
            case 'warning':
                return {secondary, color: 'yellow'};
            case 'critical':
                return {secondary, color: 'red'};
            default:
                return {secondary, color: 'blue'};
        }
    }, [variant, primaryBannerButton]);

    const isDemoAccount = user?.isDemoAccount || false;
    const canDismiss = !isDemoAccount && (!!snooze || sessionDismiss || persistDismiss);

    const shouldShow = useMemo(() => {
        if (!snooze && persistDismiss && !sessionDismiss && !user) return false;

        if (canDismiss && isClosed) return false;

        if (showForNewUsersOnly && user && !isDemoAccount) {
            const isNewAccount = user.isNewAccount;
            if (isNewAccount !== true) return false;
        }

        return true;
    }, [snooze, persistDismiss, sessionDismiss, user, canDismiss, isClosed, showForNewUsersOnly, isDemoAccount]);

    if (!shouldShow) return null;

    function onDismiss(e: React.MouseEvent) {
        if (isDemoAccount) return;

        setIsClosed(true);
        if (snooze) createSnoozeFor(snooze).recordDismissal();
        else if (sessionDismiss && sessionKey) saveToSession(sessionKey, '1');
        else if (persistDismiss && flexKey && user) {
            user.setFlex(flexKey, true);
            user.update();
        }
        if (onClose) {
            onClose(e);
        }
    }

    const getDefaultIcon = () => {
        if (icon) return icon;

        switch (variant) {
            case 'success':
                return <SuccessIcon />;
            case 'warning':
                return <WarnIcon />;
            case 'critical':
                return <ErrorIcon />;
            default:
                return <InfoIcon />;
        }
    };

    const handleAction: MouseEventHandler = e => {
        if (onAction) onAction(e);
    };

    return (
        <div className={classNames('feature-intro-banner', `variant-${variant}`, className)}>
            <div className='banner-header'>
                <div className='intro-icon'>{getDefaultIcon()}</div>

                <div className='banner-main'>
                    {title && <h3 className='intro-title'>{title}</h3>}
                    <p className='intro-text'>{children}</p>

                    {actionLabel &&
                        (onAction || props.actionRoute) &&
                        (() => {
                            const actionButton = (
                                <BluemateButton
                                    label={actionLabel}
                                    onClick={handleAction}
                                    className={'cta-button'}
                                    disabled={actionLoading}
                                    ornaments={
                                        actionLoading
                                            ? {element: <AwesomeIcon name='arrows-rotate' className='cta-spinner' />}
                                            : undefined
                                    }
                                    {...buttonProps}
                                />
                            );
                            return props.actionRoute ? (
                                <Link route={props.actionRoute}>{actionButton}</Link>
                            ) : (
                                actionButton
                            );
                        })()}
                </div>

                {(canDismiss || onClose) && (
                    <button
                        type='button'
                        className='intro-close'
                        onClick={onDismiss}
                        aria-label={t('misc.infoBanner.close', 'Sluiten')}
                    >
                        <AwesomeIcon name='circle-check' family='sharp' prefix='far' />
                    </button>
                )}
            </div>
        </div>
    );
}
